import { Alert, Box, Button, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Panel } from "../components/layout/Panel";
import {
  getChannelForService,
  updateChannelForService,
  testChannelForService,
} from "../api/client";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import {
  AlertChannelForm,
  type AlertChannelFormState,
  type ExistingConfig,
} from "../components/alerts/AlertChannelForm";

const initialFormState: AlertChannelFormState = {
  name: "",
  channelType: "webhook",
  enabled: true,
  webhookUrl: "",
  ntfyTopic: "",
  ntfyServer: "https://ntfy.sh",
  ntfyAuthType: "none",
  ntfyUser: "",
  ntfyPass: "",
  ntfyBearerToken: "",
  emailRecipients: "",
  telegramToken: "",
  telegramChatId: "",
};

export function PageAlertChannelEdit() {
  const { hierarchy, smtpConfigured } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { channelId } = useParams<{ channelId: string }>();
  const navigate = useNavigate();
  const [channel, setChannel] = useState<{
    id: string;
    name: string;
    channel_type: string;
    channel_config: Record<string, unknown>;
    enabled: boolean;
  } | null>(null);
  const [formState, setFormState] = useState<AlertChannelFormState>(initialFormState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message?: string } | null>(null);

  useEffect(() => {
    if (!serviceSlug || !channelId) return;
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    getChannelForService(serviceSlug, channelId, abort.signal)
      .then((c) => {
        if (abort.signal.aborted) return;
        setChannel({
          id: c.id,
          name: c.name,
          channel_type: c.channel_type,
          channel_config: c.channel_config ?? {},
          enabled: c.enabled,
        });
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load channel");
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [serviceSlug, channelId]);

  useEffect(() => {
    if (channel) {
      const cfg = channel.channel_config ?? {};
      const authType =
        (cfg.auth_type as string) ??
        (cfg.bearer_token_set ? "token" : cfg.password_set ? "password" : "none");
      setFormState({
        name: channel.name,
        channelType: (channel.channel_type as AlertChannelFormState["channelType"]) ?? "webhook",
        enabled: channel.enabled,
        webhookUrl: (cfg.url as string) ?? "",
        ntfyTopic: (cfg.topic as string) ?? "",
        ntfyServer: (cfg.server as string) ?? "https://ntfy.sh",
        ntfyAuthType: authType === "token" || authType === "password" ? authType : "none",
        ntfyUser: (cfg.username as string) ?? "",
        ntfyPass: (cfg.password as string) ?? "",
        ntfyBearerToken: (cfg.bearer_token as string) ?? "",
        emailRecipients: ((cfg.recipients as string[]) ?? []).join(", "),
        telegramToken: (cfg.bot_token as string) ?? "",
        telegramChatId: (cfg.chat_id as string) ?? "",
      });
    }
  }, [channel]);

  const buildConfig = (): Record<string, unknown> => {
    const t = channel?.channel_type ?? "webhook";
    switch (t) {
      case "webhook":
        return { url: formState.webhookUrl.trim() };
      case "ntfy":
        return {
          topic: formState.ntfyTopic.trim(),
          server: formState.ntfyServer.trim() || "https://ntfy.sh",
          auth_type: formState.ntfyAuthType,
          ...(formState.ntfyAuthType === "token" &&
            formState.ntfyBearerToken && { bearer_token: formState.ntfyBearerToken }),
          ...(formState.ntfyAuthType === "password" && {
            username: formState.ntfyUser,
            password: formState.ntfyPass,
          }),
        };
      case "email":
        return {
          recipients: formState.emailRecipients
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean),
        };
      case "telegram":
        return {
          bot_token: formState.telegramToken.trim(),
          chat_id: formState.telegramChatId.trim(),
        };
      default:
        return {};
    }
  };

  const validate = (): string | null => {
    const config = buildConfig();
    const cfg = channel?.channel_config as Record<string, unknown> | undefined;

    if (channel?.channel_type === "webhook" && !(config.url as string)) {
      return "URL is required";
    }
    if (channel?.channel_type === "ntfy") {
      if (!(config.topic as string)) return "Topic is required";
      if (formState.ntfyAuthType === "token") {
        const hasExisting = cfg?.bearer_token_set;
        const hasNew = formState.ntfyBearerToken.trim();
        if (!hasExisting && !hasNew) {
          return "Bearer token is required when using token authentication";
        }
      }
      if (formState.ntfyAuthType === "password") {
        const hasExisting = cfg?.password_set;
        const hasNew = formState.ntfyUser.trim() && formState.ntfyPass;
        if (!hasExisting && !hasNew) {
          return "Username and password are required when using password authentication";
        }
      }
    }
    if (channel?.channel_type === "email" && (config.recipients as string[]).length === 0) {
      return "At least one recipient is required";
    }
    if (channel?.channel_type === "telegram" && !(config.chat_id as string)) {
      return "Chat ID is required";
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!serviceSlug || !channelId || !channel) return;

    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const updated = await updateChannelForService(
        serviceSlug,
        channelId,
        { name: formState.name.trim(), enabled: formState.enabled, channel_config: buildConfig() },
        undefined,
      );
      setChannel({
        id: updated.id,
        name: updated.name,
        channel_type: updated.channel_type,
        channel_config: updated.channel_config ?? {},
        enabled: updated.enabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update channel");
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async () => {
    if (!serviceSlug || !channelId) return;
    setTesting(true);
    setTestResult(null);
    try {
      await testChannelForService(serviceSlug, channelId, undefined);
      setTestResult({ ok: true });
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : "Test failed" });
    } finally {
      setTesting(false);
    }
  };

  const existingConfig: ExistingConfig = {
    bearer_token_set: (channel?.channel_config as Record<string, unknown>)?.bearer_token_set as
      | boolean
      | undefined,
    password_set: (channel?.channel_config as Record<string, unknown>)?.password_set as
      | boolean
      | undefined,
    bot_token_set: (channel?.channel_config as Record<string, unknown>)?.bot_token_set as
      | boolean
      | undefined,
  };

  if (!serviceSlug || !channelId) {
    return <Alert severity="warning">No Service or Channel in URL.</Alert>;
  }

  if (loading || !channel) {
    return <Typography>Loading…</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Panel title={`Channel: ${channel.name || channel.channel_type}`}>
        <AlertChannelForm
          mode="edit"
          state={formState}
          onChange={(updates) => setFormState((prev) => ({ ...prev, ...updates }))}
          existingConfig={existingConfig}
          smtpConfigured={smtpConfigured}
          error={error}
          onErrorDismiss={() => setError(null)}
          submitting={submitting}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
          submitLabel="Save"
          testResult={testResult}
          onTestResultDismiss={() => setTestResult(null)}
          onTest={handleTest}
          testing={testing}
        />
      </Panel>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="text"
          onClick={() => navigate(`/services/${encodeURIComponent(serviceSlug)}/alerts`)}
        >
          Back to alerts
        </Button>
      </Box>
    </Box>
  );
}
